// Security utilities for OWASP Top 10 compliance

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - HTML string to sanitize
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return '';
  
  // Remove script tags and event handlers
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/data:/gi, '');
};

/**
 * Validate and sanitize user input
 * @param {string} input - User input to validate
 * @param {string} type - Type of validation ('email', 'text', 'url', 'number')
 * @returns {object} - { isValid: boolean, sanitized: string, error: string }
 */
export const validateInput = (input, type = 'text') => {
  if (typeof input !== 'string') {
    return { isValid: false, sanitized: '', error: 'Input must be a string' };
  }

  const trimmed = input.trim();
  
  switch (type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return {
        isValid: emailRegex.test(trimmed),
        sanitized: trimmed.toLowerCase(),
        error: emailRegex.test(trimmed) ? '' : 'Invalid email format'
      };
    
    case 'url':
      // Allow relative URLs that start with /api/ (for Docker/proxy mode)
      if (trimmed.startsWith('/api/') || trimmed.startsWith('/api')) {
        return {
          isValid: true,
          sanitized: trimmed,
          error: ''
        };
      }
      
      // For absolute URLs, validate protocol
      try {
        const url = new URL(trimmed);
        const allowedProtocols = ['http:', 'https:'];
        return {
          isValid: allowedProtocols.includes(url.protocol),
          sanitized: url.toString(),
          error: allowedProtocols.includes(url.protocol) ? '' : 'Invalid URL protocol'
        };
      } catch {
        return { isValid: false, sanitized: '', error: 'Invalid URL format' };
      }
    
    case 'number':
      const num = parseFloat(trimmed);
      return {
        isValid: !isNaN(num) && isFinite(num),
        sanitized: num.toString(),
        error: !isNaN(num) && isFinite(num) ? '' : 'Invalid number'
      };
    
    case 'text':
    default:
      // Remove potentially dangerous characters
      const sanitized = trimmed
        .replace(/[<>]/g, '')
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        .replace(/on\w+\s*=/gi, '');
      
      return {
        isValid: sanitized.length > 0 && sanitized.length <= 1000,
        sanitized,
        error: sanitized.length === 0 ? 'Input cannot be empty' : 
               sanitized.length > 1000 ? 'Input too long' : ''
      };
  }
};

/**
 * Generate CSRF token
 * @returns {string} - CSRF token
 */
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Validate CSRF token
 * @param {string} token - Token to validate
 * @param {string} expected - Expected token
 * @returns {boolean} - Whether token is valid
 */
export const validateCSRFToken = (token, expected) => {
  if (!token || !expected) return false;
  return token === expected;
};

/**
 * Rate limiting helper
 * @param {string} key - Rate limit key (e.g., user ID, IP)
 * @param {number} maxRequests - Maximum requests allowed
 * @param {number} windowMs - Time window in milliseconds
 * @returns {boolean} - Whether request is allowed
 */
const rateLimitStore = new Map();

export const checkRateLimit = (key, maxRequests = 100, windowMs = 60000) => {
  const now = Date.now();
  const windowStart = now - windowMs;
  
  if (!rateLimitStore.has(key)) {
    rateLimitStore.set(key, []);
  }
  
  const requests = rateLimitStore.get(key);
  
  // Remove old requests outside the window
  const validRequests = requests.filter(timestamp => timestamp > windowStart);
  rateLimitStore.set(key, validRequests);
  
  if (validRequests.length >= maxRequests) {
    return false;
  }
  
  // Add current request
  validRequests.push(now);
  rateLimitStore.set(key, validRequests);
  
  return true;
};

/**
 * Headers for API requests
 * Note: Security headers (X-Content-Type-Options, X-Frame-Options, etc.) 
 * and cache control headers (Cache-Control, Pragma, Expires) should only 
 * be set by the server in responses, not by the client in requests.
 * The client should only send necessary request headers like Content-Type.
 * @returns {object} - Headers object
 */
export const getSecureHeaders = () => {
  return {
    'Content-Type': 'application/json',
    // Removed all response headers - these should only be set by the server:
    // - Security headers: X-Content-Type-Options, X-Frame-Options, etc.
    // - Cache headers: Cache-Control, Pragma, Expires
    // The server controls caching and security via response headers
  };
};

/**
 * Log security events
 * @param {string} event - Security event type
 * @param {object} details - Event details
 */
export const logSecurityEvent = (event, details = {}) => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    event,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  // In production, this should be sent to a secure logging service
  console.warn('Security Event:', logEntry);
  
  // You could send this to your backend for logging
  // fetch('/api/security/log', {
  //   method: 'POST',
  //   headers: getSecureHeaders(),
  //   body: JSON.stringify(logEntry)
  // });
};

/**
 * Check for suspicious patterns in input
 * @param {string} input - Input to check
 * @returns {boolean} - Whether input contains suspicious patterns
 */
export const detectSuspiciousInput = (input) => {
  if (typeof input !== 'string') return false;
  
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /vbscript:/i,
    /on\w+\s*=/i,
    /eval\s*\(/i,
    /expression\s*\(/i,
    /url\s*\(/i,
    /@import/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<link/i,
    /<meta/i,
    /<style/i
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(input));
};
